import { FC, useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as ClockIcon } from '@/assets/clock-plus.svg';
import useIsMobileView from '@/hooks/useIsMobileView';
import { SeatDto } from '@/services/table/tableState';

import ActionButton, { ActionButtonVariant } from '../ActionButton/ActionButton';
import TimeBankTimer from '../TimeBankTimer/TimeBankTimer';
import { ReducerState } from '@/services/common/lib/reducerState';
import { timebankAction } from '@/services/table/tableActions';

interface TimeBankButtonProps {
  tableId: string;
  isUsingTimebank: boolean;
  timebank?: number;
  isActingPlayer?: boolean;
  seat?: SeatDto | null;
}

const TimeBankButton: FC<TimeBankButtonProps> = ({ tableId, isUsingTimebank, timebank, isActingPlayer, seat }) => {
  const dispatch = useDispatch();
  const isMobileView = useIsMobileView();
  const [isTimeBankActive, setIsTimeBankActive] = useState(false);
  const autoTimeBankEnabled = useSelector((state: ReducerState) => state.auth.settings?.autoTimeBankEnabled);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleOnClickTimeBank = useCallback(
    !autoTimeBankEnabled && !isTimeBankActive
      ? () => {
          dispatch(timebankAction(tableId));
          setIsTimeBankActive(!isTimeBankActive);
        }
      : () => {},
    [autoTimeBankEnabled, dispatch, isTimeBankActive, tableId],
  );

  return (
    <>
      {!isUsingTimebank ? (
        <ActionButton
          onClick={handleOnClickTimeBank}
          variant={ActionButtonVariant.TimeBank}
          containerClassName="flex justify-end items-center"
          className={twMerge(
            'mt-0 !h-[2.5em] !w-[5em] !rounded-full !border-[2px] !px-4 !py-2',
            '!text-ph-gray-silver',
            !autoTimeBankEnabled && !isTimeBankActive
              ? 'cursor-pointer hover:!border-ph-gray-white hover:!text-ph-gray-white'
              : 'cursor-default',
            isTimeBankActive &&
              '!border-ph-primary-violet !bg-ph-primary-violet !text-white hover:!border-ph-primary-violet hover:!bg-ph-primary-violet',
          )}
          isDisabled={isUsingTimebank || timebank === 0}
          isPlayerTurn={!!isActingPlayer}
        >
          <div className="flex items-center justify-center gap-1">
            <ClockIcon className={twMerge('h-5 w-5 stroke-ph-gray-silver hover:!stroke-ph-gray-white')} />
            <span className="flex items-center text-base font-normal leading-none">
              {isMobileView ? '' : '+'}
              {timebank}s
            </span>
          </div>
        </ActionButton>
      ) : (
        <div className="flex justify-end">
          <TimeBankTimer timer={seat?.timer} timeRemaining={timebank} />
        </div>
      )}
    </>
  );
};

export default TimeBankButton;
